import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { compose } from '@reduxjs/toolkit'
import queryString from 'qs'

import get from 'lodash/get'

import { titleCase } from '../utils/stringUtils'
import { Submit, Hint } from '../form'
import AuthTextField from './AuthTextField'
import AuthLocationField from './AuthLocationField'
import withCurrentVisit from './withCurrentVisit'

import { InputGroup, Center, AuthToken, Column } from './shared'

const HintWrapper = styled.div`
  margin-top: -20px;
  margin-bottom: 40px;
`

const validationSchema = Yup.object({
  user: Yup.object({
    email: Yup.string()
      .required('This field is required')
      .email('Not a valid email'),
  }),
})

const ConfirmEmail = ({
  currentUser,
  currentVisit,
  currentVisitLoading,
  location,
}) => {
  const formElement = useRef(null)
  const { source } = queryString.parse(location.search, {
    ignoreQueryPrefix: true,
  })
  const sourceName = titleCase(source)

  const form = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      user: {
        email: currentUser?.email || '',
        location: currentVisit?.location?.name || '',
        latitude: currentVisit?.location?.lat ?? null,
        longitude: currentVisit?.location?.lng ?? null,
      },
    },
  })

  useEffect(() => {
    const { dirtyFields } = form.formState
    const current = form.getValues()
    const keep = (path, fresh) =>
      get(dirtyFields, path) ? get(current, path) : fresh
    form.reset({
      user: {
        email: keep('user.email', currentUser?.email || ''),
        location: keep('user.location', currentVisit?.location?.name || ''),
        latitude: keep('user.latitude', currentVisit?.location?.lat ?? null),
        longitude: keep('user.longitude', currentVisit?.location?.lng ?? null),
      },
    })
  }, [
    currentUser?.email,
    currentVisit?.location?.name,
    currentVisit?.location?.lat,
    currentVisit?.location?.lng,
    form,
  ])

  return (
    <FormProvider {...form}>
      <form
        className="qa-confirm-email"
        action={`/users/${currentUser.id}`}
        method="post"
        ref={formElement}
        onSubmit={form.handleSubmit(() => formElement.current.submit())}
      >
        <AuthToken />
        <input type="hidden" name="_method" value="patch" />

        <div className="container">
          <div className="row">
            <Column className="col-xs-12 col-md-6 col-md-push-3">
              <AuthTextField
                label="Email"
                name="user[email]"
                type="email"
                lessMargin
              />

              <HintWrapper>
                {currentUser.email ? (
                  <Hint>
                    {sourceName} gave us this email address. Please confirm that
                    this is one you want to use for your Riparide account. If
                    it&apos;s not, you can change it now.
                  </Hint>
                ) : (
                  <Hint>
                    {sourceName} didn&apos;t give us your email address. We need
                    this to communicate with you about bookings. Please enter
                    your email address now.
                  </Hint>
                )}
              </HintWrapper>

              <AuthLocationField ready={!currentVisitLoading} />

              <InputGroup>
                <Center>
                  <Submit name="commit" disabled={currentVisitLoading}>
                    Continue
                  </Submit>
                </Center>
              </InputGroup>
            </Column>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default compose(
  withRouter,
  withCurrentVisit,
  connect(({ auth }, { currentVisit, currentVisitLoading }) => ({
    currentUser: auth.currentUser,
    currentVisit,
    currentVisitLoading,
  }))
)(ConfirmEmail)
